import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useProfile } from "../Components/Hooks/UserHooks";
import { hasFeature, FEATURES } from "../helpers/feature_flags_helper";

// Filter menu items recursively by `requiredFeature` field.
// Items without `requiredFeature` are always shown.
const filterByFeatures = (items) => {
  if (!Array.isArray(items)) return items;
  return items
    .filter((it) => !it.requiredFeature || hasFeature(it.requiredFeature))
    .map((it) =>
      it.subItems
        ? { ...it, subItems: filterByFeatures(it.subItems) }
        : it
    );
};

// Pure function that builds menu items (no hooks, can be called from useEffect)
export const buildMenuItems = (userProfile, history) => {
  console.warn("🔥 buildMenuItems called with userProfile:", userProfile);
  console.warn("🔥 buildMenuItems isAdmin:", userProfile?.isAdmin);
  
  // If user is admin, show ONLY admin menu
  if (userProfile?.isAdmin) {
    const adminMenuItems = [
      {
        label: "Admin",
        isHeader: true,
      },
      {
        id: "admin",
        label: "Overview",
        icon: "ri-dashboard-2-fill",
        link: "/admin",
        click: function (e) {
          e.preventDefault();
          history("/admin");
        },
      },
      {
        id: "admin-users",
        label: "Users",
        icon: "ri-group-fill",
        link: "/admin/users",
        click: function (e) {
          e.preventDefault();
          history("/admin/users");
        },
      },
      {
        id: "admin-payments",
        label: "Payments",
        icon: "ri-wallet-fill",
        link: "/admin/payments",
        click: function (e) {
          e.preventDefault();
          history("/admin/payments");
        },
      },
      {
        id: "admin-support-tickets",
        label: "Support Tickets",
        icon: "ri-customer-service-2-fill",
        link: "/admin/support-tickets",
        click: function (e) {
          e.preventDefault();
          history("/admin/support-tickets");
        },
      },
      {
        id: "admin-plan-requests",
        label: "Plan Upgrades",
        icon: "ri-vip-crown-fill",
        link: "/admin/plan-upgrade-requests",
        click: function (e) {
          e.preventDefault();
          history("/admin/plan-upgrade-requests");
        },
      },
      {
        id: "admin-settings",
        label: "Settings",
        icon: "ri-settings-3-fill",
        link: "/admin/settings",
        click: function (e) {
          e.preventDefault();
          history("/admin/settings");
        },
      },
      {
        id: "admin-more",
        label: "More",
        icon: "ri-more-2-fill",
        click: function (e) {
          e.preventDefault();
        },
        subItems: [
          {
            id: "admin-invoices",
            label: "Invoices",
            link: "/admin/invoices",
            click: function (e) {
              e.preventDefault();
              history("/admin/invoices");
            },
          },
          {
            id: "admin-contact-enquiries",
            label: "Contact Enquiries",
            link: "/admin/contact-enquiries",
            click: function (e) {
              e.preventDefault();
              history("/admin/contact-enquiries");
            },
          },
          {
            id: "admin-docs",
            label: "Admin Guide",
            link: "/admin/docs",
            click: function (e) {
              e.preventDefault();
              history("/admin/docs");
            },
          },
        ],
      },
    ];
    console.warn("🔥 buildMenuItems returning ADMIN menu with", adminMenuItems.length, "items");
    return adminMenuItems;
  }
  
  // For regular users, show regular menu
  // Read professionType with sessionStorage fallback to handle null userProfile on first render
  const professionType = userProfile?.professionType || (() => {
    try { return JSON.parse(sessionStorage.getItem('authUser') || '{}').professionType || 'SERVICE_PROFESSIONAL'; }
    catch { return 'SERVICE_PROFESSIONAL'; }
  })();
  const isClientLess = professionType === 'STOCK_TRADER' || professionType === 'PERSONAL_PRODUCTIVITY';
  const defaultMenuItems = [
    {
      label: "Menu",
      isHeader: true,
    },
    {
      id: "new-activity",
      label: "Add Service / Call",
      icon: "ri-add-circle-fill",
      link: "/add-task",
      click: function (e) {
        e.preventDefault();
        history("/add-task");
      },
    },
    {
      id: "dashboard",
      label: "Dashboard",
      icon: "ri-dashboard-2-fill",
      link: "/dashboard",
      click: function (e) {
        e.preventDefault();
        history("/dashboard");
      },
    },
    {
      id: "clients",
      label: "Customers",
      icon: "ri-group-2-fill",
      link: "/customers",
      click: function (e) {
        e.preventDefault();
        history("/customers");
      },
    },
    {
      id: "leads",
      label: "Leads",
      icon: "ri-funnel-line",
      link: "/leads",
      requiredFeature: FEATURES.LEADS,
      click: function (e) {
        e.preventDefault();
        history("/leads");
      },
    },
    {
      id: "widgets",
      label: "Lead Widgets",
      icon: "ri-code-box-line",
      link: "/widgets",
      requiredFeature: FEATURES.LEAD_WIDGET,
      click: function (e) {
        e.preventDefault();
        history("/widgets");
      },
    },
    {
      id: "forms",
      label: "Lead Forms",
      icon: "ri-file-list-3-line",
      link: "/forms",
      click: function (e) {
        e.preventDefault();
        history("/forms");
      },
    },
    {
      id: "sequences",
      label: "Sequences",
      icon: "ri-flow-chart",
      link: "/sequences",
      requiredFeature: FEATURES.SEQUENCES,
      click: function (e) {
        e.preventDefault();
        history("/sequences");
      },
    },
    {
      id: "affiliate",
      label: "Affiliate",
      icon: "ri-money-dollar-circle-line",
      link: "/affiliate",
      click: function (e) {
        e.preventDefault();
        history("/affiliate");
      },
    },
    {
      id: "reminders",
      label: "Reminders",
      icon: "ri-notification-badge-fill",
      link: "/reminders",
      click: function (e) {
        e.preventDefault();
        history("/reminders");
      },
    },
    {
      id: "calendar",
      label: "Calendar",
      icon: "ri-calendar-2-fill",
      link: "/calendar",
      click: function (e) {
        e.preventDefault();
        history("/calendar");
      },
    },
    {
      id: "more",
      label: "More",
      icon: "ri-more-2-fill",
      click: function (e) {
        e.preventDefault();
      },
      subItems: [
        {
          id: "website-builder",
          label: "AI Website",
          icon: "ri-global-line",
          link: "/my-websites",
          click: function (e) {
            e.preventDefault();
            history("/my-websites");
          },
        },
        {
          id: "invoices",
          label: "Invoices",
          icon: "ri-bill-fill",
          link: "/invoices",
          click: function (e) {
            e.preventDefault();
            history("/invoices");
          },
        },
        {
          id: "email-templates",
          label: "Email Templates",
          icon: "ri-mail-open-fill",
          link: "/email-templates",
          click: function (e) {
            e.preventDefault();
            history("/email-templates");
          },
        },
        {
          id: "analytics",
          label: "Analytics",
          icon: "ri-bar-chart-2-fill",
          link: "/analytics",
          click: function (e) {
            e.preventDefault();
            history("/analytics");
          },
        },
        // WhatsApp Inbox hidden for email-only MVP (page/route code retained).
        {
          id: "help",
          label: "Help",
          icon: "ri-question-line",
          link: "/help",
          click: function (e) {
            e.preventDefault();
            history("/help");
          },
        },
        {
          id: "docs",
          label: "User Guide",
          icon: "ri-book-open-fill",
          link: "/docs",
          click: function (e) {
            e.preventDefault();
            history("/docs");
          },
        },
        {
          id: "support-tickets",
          label: "Support Tickets",
          icon: "ri-mail-open-line",
          link: "/support-tickets",
          click: function (e) {
            e.preventDefault();
            history("/support-tickets");
          },
        },
        {
          id: "referral",
          label: "Refer & Earn",
          icon: "ri-gift-2-line",
          link: "/referral",
          click: function (e) {
            e.preventDefault();
            history("/referral");
          },
        },
        {
          id: "team",
          label: "Team",
          icon: "ri-team-line",
          link: "/team",
          requiredFeature: FEATURES.TEAM,
          click: function (e) {
            e.preventDefault();
            history("/team");
          },
        },
        {
          id: "leaderboard",
          label: "Leaderboard",
          icon: "ri-trophy-line",
          link: "/team/leaderboard",
          requiredFeature: FEATURES.TEAM,
          click: function (e) {
            e.preventDefault();
            history("/team/leaderboard");
          },
        },

        {
          id: "billing",
          label: "Billing",
          icon: "ri-secure-payment-line",
          link: "/billing",
          click: function (e) {
            e.preventDefault();
            history("/billing");
          },
        },
        {
          id: "settings",
          label: "Settings",
          icon: "ri-settings-3-fill",
          link: "/settings",
          click: function (e) {
            e.preventDefault();
            history("/settings");
          },
        },
        {
          id: "feedback",
          label: "Share Feedback",
          icon: "ri-chat-smile-2-line",
          link: "https://forms.gle/leadwix-feedback",
          click: function (e) {
            e.preventDefault();
            window.open("https://forms.gle/leadwix-feedback", "_blank", "noopener");
          },
        },
      ],
    },
    // Mobile-only menu items - shown only on small screens (after More menu)
    {
      id: "mobile-profile",
      label: "Profile",
      icon: "ri-user-fill",
      link: "/profile",
      click: function (e) {
        e.preventDefault();
        history("/profile");
      },
      className: "d-md-none",
    },
    {
      id: "mobile-logout",
      label: "Logout",
      icon: "ri-logout-circle-r-line",
      link: "/logout",
      click: function (e) {
        e.preventDefault();
        history("/logout");
      },
      className: "d-md-none",
    },
  ];

  // Hide client-specific menu items for professions that have no clients
  if (isClientLess) {
    const clientIdx = defaultMenuItems.findIndex(i => i.id === 'clients');
    if (clientIdx !== -1) defaultMenuItems.splice(clientIdx, 1);
    const moreItem = defaultMenuItems.find(i => i.id === 'more');
    if (moreItem?.subItems) {
      moreItem.subItems = moreItem.subItems.filter(s => s.id !== 'invoices');
    }
  }

  console.warn("🔥 buildMenuItems returning REGULAR menu with", defaultMenuItems.length, "items");
  console.warn("🔥 Full menu items array:", defaultMenuItems.map(item => ({ id: item.id, label: item.label, className: item.className })));
  return filterByFeatures(defaultMenuItems);
};

// Create a hook that returns menu items
const useMenuItems = () => {
  const history = useNavigate();
  const { userProfile } = useProfile();

  // Debug logging
  useEffect(() => {
    console.log("useMenuItems - userProfile:", userProfile);
    console.log("useMenuItems - userProfile?.isAdmin:", userProfile?.isAdmin);
  }, [userProfile]);

  // Memoize getMenuItems so it only recreates when userProfile changes
  const getMenuItems = useMemo(() => {
    return () => buildMenuItems(userProfile, history);
  }, [userProfile, history]);

  return getMenuItems();
};

// Navdata is a component that wraps the hook and returns it as a component
const Navdata = () => {
  const menuItems = useMenuItems();
  return <React.Fragment>{menuItems}</React.Fragment>;
};

export default Navdata;
